import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, User } from 'lucide-react';
import { Button } from '../components/ui/button';

const Blog = () => {
  const blogPosts = [
    {
      id: 1,
      slug: 'best-time-visit-washington-dc',
      title: 'The Best Time to Visit Washington DC: A Seasonal Guide',
      excerpt: 'Discover the perfect season for your DC getaway. From cherry blossom season to holiday festivities, we break down what each season offers visitors.',
      image: 'https://images.unsplash.com/photo-1759020622073-49edf74dea96?w=800&q=80',
      category: 'Travel Tips',
      author: 'Capitol View Stay',
      date: 'January 15, 2025',
      readTime: '5 min read'
    },
    {
      id: 2,
      slug: 'capitol-hill-neighborhood-guide',
      title: 'Capitol Hill Neighborhood Guide: Where to Eat, Drink & Explore',
      excerpt: 'Your insider guide to Capitol Hill - from the best brunch spots at Eastern Market to hidden gems locals love. Make the most of your stay.',
      image: 'https://images.unsplash.com/photo-1501466044931-62695aada8e9?w=800&q=80',
      category: 'Local Guide',
      author: 'Capitol View Stay',
      date: 'January 10, 2025',
      readTime: '7 min read'
    },
    {
      id: 3,
      slug: 'free-things-to-do-washington-dc',
      title: '15 Free Things to Do in Washington DC',
      excerpt: 'DC is one of the best cities for free attractions. From world-class Smithsonian museums to iconic monuments, here\'s how to explore without spending a dime.',
      image: 'https://images.unsplash.com/photo-1617581629397-a72507c3de9e?w=800&q=80',
      category: 'Things to Do',
      author: 'Capitol View Stay',
      date: 'January 5, 2025',
      readTime: '6 min read'
    },
    {
      id: 4,
      slug: 'work-from-dc-remote-work-guide',
      title: 'Working Remotely from Washington DC: The Ultimate Guide',
      excerpt: 'Planning an extended stay? Our properties are perfect for remote workers. Discover the best cafes, coworking spaces, and tips for a productive DC workation.',
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80',
      category: 'Remote Work',
      author: 'Capitol View Stay',
      date: 'December 28, 2024',
      readTime: '8 min read'
    },
    {
      id: 5,
      slug: 'family-friendly-dc-activities',
      title: 'Family-Friendly Activities in Washington DC',
      excerpt: 'Traveling with kids? DC offers endless educational and fun experiences for families. Here are our top picks for an unforgettable family vacation.',
      image: 'https://images.unsplash.com/photo-1555761001-768e682ff844?w=800&q=80',
      category: 'Family Travel',
      author: 'Capitol View Stay',
      date: 'December 20, 2024',
      readTime: '6 min read'
    },
    {
      id: 6,
      slug: 'dc-cherry-blossom-guide',
      title: 'Cherry Blossom Season in DC: Everything You Need to Know',
      excerpt: 'The National Cherry Blossom Festival is DC\'s most magical time. Learn when to visit, where to see the best blooms, and how to avoid the crowds.',
      image: 'https://images.unsplash.com/photo-1648337203513-54c2da0a5147?w=800&q=80',
      category: 'Events',
      author: 'Capitol View Stay',
      date: 'December 15, 2024',
      readTime: '5 min read'
    }
  ];

  const categories = ['All', 'Travel Tips', 'Local Guide', 'Things to Do', 'Remote Work', 'Family Travel', 'Events'];
  const [activeCategory, setActiveCategory] = React.useState('All');

  const filteredPosts = activeCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === activeCategory);

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Hero */}
      <section className="bg-slate-900 text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Capitol View Stay Blog</h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            Insider tips, local guides, and everything you need to plan your perfect Washington DC getaway.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-white border-b border-slate-200 sticky top-[72px] z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 py-4 overflow-x-auto scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  activeCategory === category
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <article 
                key={post.id} 
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 group"
              >
                {/* Image */}
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Category */}
                  <span className="inline-block px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full mb-3">
                    {post.category}
                  </span>

                  {/* Title */}
                  <h2 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-amber-600 transition-colors line-clamp-2">
                    {post.title}
                  </h2>

                  {/* Excerpt */}
                  <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>

                  {/* Meta */}
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        {post.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {post.readTime}
                      </span>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Load More */}
          <div className="mt-12 text-center">
            <Button variant="outline" className="border-slate-300">
              Load More Articles
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-12 md:py-16 bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Stay Updated</h2>
          <p className="text-slate-300 mb-6">
            Get the latest DC travel tips and exclusive deals delivered to your inbox.
          </p>
          <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-6">
              Subscribe
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Blog;
